import React, { useState, useMemo, useEffect } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender,
    createColumnHelper,
} from '@tanstack/react-table';
import {
    ArrowUpIcon,
    ArrowDownIcon,
    ArrowLeftIcon,
    MagnifyingGlassIcon,
    TrashIcon,
    ArrowPathIcon,
    FolderIcon
} from '@heroicons/react/24/outline';
import DataTable from '../../components/ui/DataTable';
import { apiRequest } from '../../api/api';

// --- 2. Optimized & Clean Cell Components (Click-to-Edit Pattern) ---
const DataCell = React.memo(({ getValue, row: { index }, column: { id }, table, type = 'text' }) => {
    const initialValue = getValue();
    const [value, setValue] = useState(initialValue);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const onBlur = () => {
        setIsEditing(false);
        if (value !== initialValue) {
            let finalValue = value;
            if (type === 'number') {
                const parsed = parseFloat(value);
                finalValue = isNaN(parsed) ? 0 : parsed;
            }
            table.options.meta?.updateData(index, id, finalValue);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
    };

    if (!isEditing) {
        let displayValue = value;
        if (type === 'number') {
            displayValue = (value || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
        if (value === '' || value === null || value === undefined) {
            displayValue = '-';
        }

        return (
            <div
                onClick={() => setIsEditing(true)}
                className={`w-full h-full min-h-[24px] flex items-center px-1 cursor-pointer border border-transparent hover:border-blue-300 hover:bg-blue-50 rounded transition-all text-xs ${type === 'number' ? 'justify-end font-mono' : 'justify-start'}`}
                title="Düzenle"
            >
                {displayValue}
            </div>
        );
    }

    return (
        <input
            autoFocus
            type={type === 'number' ? 'number' : 'text'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            className={`w-full bg-white dark:bg-gray-800 border border-blue-500 rounded px-1 py-0.5 text-xs shadow-sm outline-none ${type === 'number' ? 'text-right font-mono' : 'text-left'}`}
        />
    );
});

const SelectDataCell = React.memo(({ getValue, row: { index }, column: { id }, table, options }) => {
    const initialValue = getValue();
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        setValue(initialValue?.toString() || '');
    }, [initialValue]);

    const onChange = (e) => {
        const v = e.target.value;
        setValue(v);
        table.options.meta?.updateData(index, id, v);
    };

    return (
        <select
            value={value}
            onChange={onChange}
            className="w-full bg-transparent border-none p-0 text-xs focus:ring-0 cursor-pointer text-gray-900 dark:text-gray-100 h-[24px]"
        >
            <option value="">Seçiniz</option>
            {options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    );
});

// --- 3. Main Page Logic (Hiyerarşik Yapı) ---
const TestBudgetGrid = () => {
    // View State: 'GROUPS' veya 'DETAILS'
    // Selected Group: Detayına girilen grup objesi
    const [viewMode, setViewMode] = useState('GROUPS');
    const [selectedMainGroup, setSelectedMainGroup] = useState(null);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [references, setReferences] = useState({
        maingroups: [],
        markets: [],
        subgroups: [],
        types: []
    });

    // Verileri çek
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const [regRes, mgRes, mkRes, sgRes, tyRes] = await Promise.all([
                    apiRequest('/marketing-budget/registries/'),
                    apiRequest('/marketing-budget/maingroups/'),
                    apiRequest('/marketing-budget/markets/'),
                    apiRequest('/marketing-budget/subgroups/'),
                    apiRequest('/marketing-budget/types/')
                ]);

                setReferences({
                    maingroups: (mgRes.results || mgRes).map(m => ({ value: m.id, label: m.maingroup_name })),
                    markets: (mkRes.results || mkRes).map(m => ({ value: m.id, label: m.budget_market_name })),
                    subgroups: (sgRes.results || sgRes).map(s => ({ value: s.id, label: s.subgroup_name, maingroup_id: s.maingroup })),
                    types: (tyRes.results || tyRes).map(t => ({ value: t.id, label: t.budget_type }))
                });

                setData(regRes.results || regRes || []);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    // --- VIEW MODE 1: GROUPS GRID (TABLO GÖRÜNÜMÜ) ---
    const renderGroupsView = () => {
        if (loading) return <div className="p-8 text-center">Yükleniyor...</div>;

        // Ana gruplara göre veriyi özetle
        const groupSummary = references.maingroups.map(group => {
            // Bu gruba ait kayıtları bul
            const groupData = data.filter(item => item.maingroup === group.value || item.maingroup_name === group.label);
            const totalAmount = groupData.reduce((sum, item) => sum + parseFloat(item.budget_amount || 0), 0);

            return {
                ...group,
                count: groupData.length,
                total: totalAmount
            };
        });

        // Gruplar Tablosu için kolonlar
        const columnHelper = createColumnHelper();
        const groupColumns = [
            columnHelper.accessor('label', {
                header: 'Ana Grup Adı',
                cell: info => <span className="font-medium text-gray-900 dark:text-gray-100">{info.getValue()}</span>
            }),
            columnHelper.accessor('count', {
                header: 'Kayıt Sayısı',
                size: 100,
                cell: info => <span className="text-gray-500">{info.getValue()}</span>
            }),
            columnHelper.accessor('total', {
                header: 'Toplam Bütçe',
                cell: info => <span className="font-bold text-gray-900 dark:text-gray-100">{info.getValue().toLocaleString('tr-TR', { minimumFractionDigits: 2 })} €</span>
            })
        ];

        return (
            <div className="animate-fade-in">
                <DataTable
                    data={groupSummary}
                    columns={groupColumns}
                    updateData={() => { }} // Gruplar salt okunur
                    isLoading={false}
                    onRowClick={(row) => {
                        setSelectedMainGroup(row);
                        setViewMode('DETAILS');
                    }}
                />
            </div>
        );
    };

    // --- VIEW MODE 2: DETAILS GRID (MATRIX VIEW) ---
    const renderDetailsView = () => {
        // 1. İlgili kayıtları ve alt grupları filtrele
        const groupRows = data.filter(item =>
            item.maingroup === selectedMainGroup.value ||
            item.maingroup_name === selectedMainGroup.label
        );

        const relatedSubgroups = references.subgroups.filter(sg => sg.maingroup_id === selectedMainGroup.value);

        // 2. Veriyi PIVOTLA
        const pivotedDataMap = new Map();

        groupRows.forEach(row => {
            const uniqueKey = [
                row.market || '',
                row.country || '',
                row.hotel_name || '',
                row.budget_year || '',
                row.budget_month || '',
                row.budgettype || ''
            ].join('|');

            if (!pivotedDataMap.has(uniqueKey)) {
                pivotedDataMap.set(uniqueKey, {
                    _rowId: uniqueKey,
                    market: row.market,
                    country: row.country,
                    hotel_name: row.hotel_name,
                    budget_year: row.budget_year,
                    budget_month: row.budget_month,
                    budgettype: row.budgettype,
                    subgroupValues: {}
                });
            }

            const currentRow = pivotedDataMap.get(uniqueKey);
            if (row.subgroup) {
                currentRow.subgroupValues[row.subgroup] = {
                    amount: parseFloat(row.budget_amount || 0),
                    id: row.id
                };
            }
        });

        const pivotedData = Array.from(pivotedDataMap.values());

        // 3. Kolonları Oluştur
        const columnHelper = createColumnHelper();

        // Sabit Sol Kolonlar
        const baseColumns = [
            columnHelper.accessor('market', {
                header: 'Pazar',
                cell: props => <SelectDataCell {...props} options={references.markets} />
            }),
            columnHelper.accessor('country', {
                header: 'Ülke',
                cell: props => <DataCell {...props} type="text" />
            }),
            columnHelper.accessor('hotel_name', {
                header: 'Otel',
                cell: props => <DataCell {...props} type="text" />
            }),
            columnHelper.accessor('budget_month', {
                header: 'Ay',
                size: 50,
                cell: info => <span className="text-center block w-full">{info.getValue()}</span>
            }),
            columnHelper.accessor('budgettype', {
                header: 'Tip',
                cell: props => <SelectDataCell {...props} options={references.types} />
            }),
        ];

        // Dinamik Sağ Kolonlar (Alt Gruplar) - Click-to-Edit
        const dynamicColumns = relatedSubgroups.map(sg =>
            columnHelper.accessor(row => row.subgroupValues[sg.value]?.amount || 0, {
                id: `sg_${sg.value}`,
                header: sg.label,
                cell: props => (
                    <DataCell
                        {...props}
                        type="number" // Sayısal mod
                    />
                )
            })
        );

        // Toplam tutar kolonu
        const totalColumn = columnHelper.display({
            id: 'total',
            header: 'TOPLAM',
            cell: info => {
                const values = info.row.original.subgroupValues;
                const total = Object.values(values).reduce((sum, v) => sum + (v.amount || 0), 0);
                return <div className="font-bold text-gray-900 dark:text-gray-100 text-right pr-2">{total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</div>;
            }
        });

        const finalColumns = [...baseColumns, ...dynamicColumns, totalColumn];

        return (
            <div className="animate-fade-in-up">
                {relatedSubgroups.length === 0 ? (
                    <div className="p-4 bg-yellow-50 text-yellow-800 rounded border border-yellow-200">
                        Bu ana gruba bağlı hiç alt grup bulunamadı.
                    </div>
                ) : (
                    <DataTable
                        data={pivotedData}
                        columns={finalColumns}
                        updateData={(rowIndex, colId, val) => {
                            // Update Log
                            console.log("Update:", rowIndex, colId, val);
                        }}
                        isLoading={false}
                        stickyColumnCount={3}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="p-8">
            <div className="flex justify-between items-center mb-6">
                <div>
                    {viewMode === 'DETAILS' && (
                        <button
                            onClick={() => setViewMode('GROUPS')}
                            className="text-sm font-medium text-gray-500 hover:text-blue-600 mb-2 flex items-center transition-colors"
                        >
                            <ArrowLeftIcon className="w-4 h-4 mr-1" /> Tüm Gruplara Dön
                        </button>
                    )}
                    <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
                        {viewMode === 'GROUPS' ? 'Bütçe Grupları' : selectedMainGroup?.label}
                    </h1>
                    <p className="mt-2 text-gray-600 dark:text-gray-400">
                        {viewMode === 'GROUPS'
                            ? 'Yönetmek istediğiniz ana bütçe grubunu seçiniz.'
                            : `${selectedMainGroup?.label} altındaki kalemlerin detaylı listesi ve düzenlemesi.`
                        }
                    </p>
                </div>

                {
                    viewMode === 'DETAILS' && (
                        <div className="flex gap-3">
                            <button className="px-4 py-2 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 text-sm">Excel İndir</button>
                            <button className="px-4 py-2 bg-blue-600 text-white rounded shadow-sm hover:bg-blue-700 text-sm font-medium">Değişiklikleri Kaydet</button>
                        </div>
                    )
                }
            </div >

            {viewMode === 'GROUPS' ? renderGroupsView() : renderDetailsView()}
        </div >
    );
};

export default TestBudgetGrid;
